/**
 * components/help/HelpSearchBar.jsx — Phase 84.15
 *
 * Client-side fuzzy search over the manifest (titles + summaries).
 * Uses fuse.js. Instant, no backend call. Results drop down beneath
 * the input, clicking one navigates to that article.
 */
import React, { useMemo, useRef, useState, useEffect } from 'react';
import Fuse from 'fuse.js';
import { Search } from 'lucide-react';

export default function HelpSearchBar({ manifest, onPick, autoFocus }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);

  const items = useMemo(() => {
    if (!manifest?.categories) return [];
    return manifest.categories.flatMap((c) =>
      (c.articles || []).map((a) => ({
        slug: a.slug,
        title: a.title,
        summary: a.summary || '',
        category_slug: c.slug,
        category_label: c.label,
      })),
    );
  }, [manifest]);

  const fuse = useMemo(
    () => new Fuse(items, {
      keys: [
        { name: 'title', weight: 0.7 },
        { name: 'summary', weight: 0.3 },
      ],
      threshold: 0.4,
      ignoreLocation: true,
    }),
    [items],
  );

  const results = q.trim() ? fuse.search(q).slice(0, 8).map((r) => r.item) : [];

  useEffect(() => { setActiveIdx(0); }, [q]);
  useEffect(() => { if (autoFocus) inputRef.current?.focus(); }, [autoFocus]);

  const pick = (item) => {
    onPick?.(item.category_slug, item.slug);
    setOpen(false);
    setQ('');
  };

  const onKeyDown = (e) => {
    if (!results.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); pick(results[activeIdx]); }
    else if (e.key === 'Escape') { setOpen(false); setQ(''); }
  };

  return (
    <div className="relative" data-testid="help-search">
      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={onKeyDown}
          placeholder="Search help articles..."
          className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          data-testid="help-search-input"
        />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-40 mt-1 left-0 right-0 rounded-xl border border-border bg-background shadow-lg overflow-hidden" data-testid="help-search-results">
          {results.map((r, i) => (
            <button
              key={r.slug}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(r); }}
              onMouseEnter={() => setActiveIdx(i)}
              className={`w-full text-left px-3 py-2.5 border-b border-border/40 last:border-b-0 ${
                i === activeIdx ? 'bg-primary/8' : 'hover:bg-muted/40'
              }`}
              data-testid={`help-search-result-${r.slug}`}
            >
              <div className="text-sm font-medium text-foreground">{r.title}</div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mt-0.5">{r.category_label}</div>
              {r.summary ? (
                <div className="text-xs text-muted-foreground mt-1 line-clamp-1">{r.summary}</div>
              ) : null}
            </button>
          ))}
        </div>
      )}
      {open && q.trim() && !results.length && (
        <div className="absolute z-40 mt-1 left-0 right-0 rounded-xl border border-border bg-background shadow-lg p-4 text-sm text-muted-foreground">
          No help articles match “{q}”.
        </div>
      )}
    </div>
  );
}
